import { useRef } from "react";
import { motion, useDragControls, useTransform } from "framer-motion";
import { toast } from "sonner";
import { useDemo } from "../../context/DemoContext";
import { people } from "../../data/demoContent";
import { ConversationList } from "./ConversationList";
import { ChatView } from "./ChatView";

const DRAG_SNAPS = ["expanded", "split", "peek"];
const CYCLE = { min: "peek", peek: "split", split: "expanded", expanded: "peek" };

export const ChatBottomSheet = ({ y, H, snaps, goTo }) => {
  const { mode, selected, setSelected, markInteraction, setZoom } = useDemo();
  const controls = useDragControls();
  const dragged = useRef(false);
  const visibleH = useTransform(y, (v) => Math.max(0, H - v));
  const showChat = (mode === "split" || mode === "expanded") && !people[selected].isMe;

  const onDragEnd = (_, info) => {
    const projected = y.get() + info.velocity.y * 0.15;
    const best = DRAG_SNAPS.reduce((a, b) => (Math.abs(snaps[b] - projected) < Math.abs(snaps[a] - projected) ? b : a));
    markInteraction();
    goTo(best === "split" && people[selected].isMe ? "peek" : best);
  };

  const onHandleClick = () => {
    if (dragged.current) {
      dragged.current = false;
      return;
    }
    markInteraction();
    const next = CYCLE[mode];
    goTo(next !== "peek" && people[selected].isMe ? "peek" : next);
  };

  const openConversation = (id) => {
    markInteraction();
    setSelected(id);
    goTo("split");
  };

  const onAiAction = (card) => {
    markInteraction();
    setSelected(card.action.person);
    setZoom(true);
    goTo(card.action.mode);
    toast("Opening Shashin's Garba story", { description: "Suggested by AI · sample demo" });
  };

  return (
    <motion.div
      data-testid="chat-bottom-sheet"
      data-mode={mode}
      drag="y"
      dragControls={controls}
      dragListener={false}
      dragConstraints={{ top: snaps.expanded, bottom: mode === "min" ? snaps.min : snaps.peek }}
      dragElastic={0.08}
      dragMomentum={false}
      onDragStart={() => {
        dragged.current = true;
      }}
      onDragEnd={onDragEnd}
      style={{ y, height: H }}
      className="sheet absolute inset-x-0 top-0 z-20"
    >
      <motion.div style={{ height: visibleH }} className="flex flex-col">
        <div className="flex shrink-0 cursor-grab touch-none justify-center pb-3 pt-3.5 active:cursor-grabbing" onPointerDown={(e) => controls.start(e)}>
          <button
            type="button"
            onClick={onHandleClick}
            data-testid="chat-bottom-sheet-handle"
            aria-label={`Resize chat sheet (currently ${mode}). Drag, or press to cycle.`}
            className="sheet-handle"
          />
        </div>
        {showChat ? (
          <ChatView personId={selected} onBack={() => { markInteraction(); goTo("peek"); }} onAiAction={onAiAction} />
        ) : (
          <ConversationList selected={selected} onOpen={openConversation} />
        )}
      </motion.div>
    </motion.div>
  );
};
